import {
    PUBLIC_CMS_API_BASE_URL,
    resolvePublicCmsMediaUrl,
} from '../config/api';

/**
 * @typedef {{
 *   id: string,
 *   title: string,
 *   summary: string,
 *   order: number,
 *   isActive: boolean,
 *   publishDateUtc?: string | null,
 *   createdAtUtc: string,
 *   coverImageUrl: string,
 * }} CmsNewsItem
 */

/**
 * @typedef {{
 *   totalCount: number,
 *   totalPages: number,
 *   currentPage: number,
 *   pageSize: number,
 *   hasNext: boolean,
 *   hasPrevious: boolean,
 * }} CmsNewsListMeta
 */

/**
 * @param {CmsNewsItem} item
 * @returns {{ id: string, imageUrl: string, title: string, description: string, link: string }}
 */
export function mapCmsNewsToBannerSlide(item) {
    return {
        id: item.id,
        imageUrl: resolvePublicCmsMediaUrl(item.coverImageUrl),
        title: item.title,
        description: item.summary || '',
        link: `/news/${encodeURIComponent(item.id)}`,
    };
}

/**
 * Haber kartı / Haberler bileşeni satırı.
 * @param {CmsNewsItem} item
 * @returns {{ id: string, imageUrl: string, publishedAt: string, title: string, excerpt: string, href: string }}
 */
export function mapCmsNewsToHaberItem(item) {
    return {
        id: item.id,
        imageUrl: resolvePublicCmsMediaUrl(item.coverImageUrl),
        publishedAt: item.publishDateUtc || item.createdAtUtc || '',
        title: item.title,
        excerpt: item.summary || '',
        href: `/news/${encodeURIComponent(item.id)}`,
    };
}

/**
 * @param {unknown[]} list
 * @returns {CmsNewsItem[]}
 */
function processCmsNewsList(list) {
    return /** @type {CmsNewsItem[]} */ (list)
        .filter(
            (x) =>
                x &&
                typeof x === 'object' &&
                /** @type {{ isActive?: boolean }} */ (x).isActive === true,
        )
        .sort(
            (a, b) =>
                (Number(a.order) || 0) - (Number(b.order) || 0),
        );
}

/**
 * @param {{ pageNumber?: number, pageSize?: number }} opts
 */
async function fetchCmsNewsGetAllRaw(opts) {
    const pageNumber = opts.pageNumber ?? 1;
    const pageSize = opts.pageSize ?? 50;
    const params = new URLSearchParams({
        pageNumber: String(pageNumber),
        pageSize: String(pageSize),
    });
    const url = `${PUBLIC_CMS_API_BASE_URL}/cms/News/GetAll?${params}`;
    const res = await fetch(url, {
        method: 'GET',
        headers: {
            Accept: 'application/json',
        },
    });

    const text = await res.text();
    /** @type {unknown} */
    let body;
    try {
        body = text ? JSON.parse(text) : {};
    } catch {
        throw new Error('Haber listesi okunamadı');
    }

    if (!res.ok) {
        const msg =
            body &&
            typeof body === 'object' &&
            'message' in body &&
            typeof /** @type {{ message?: string }} */ (body).message ===
                'string'
                ? /** @type {{ message: string }} */ (body).message
                : null;
        throw new Error(msg || `Haber listesi alınamadı (${res.status})`);
    }

    const obj = body && typeof body === 'object' ? body : {};
    if (/** @type {{ success?: boolean }} */ (obj).success === false) {
        const msg =
            typeof /** @type {{ message?: string | null }} */ (obj).message ===
            'string'
                ? /** @type {{ message: string }} */ (obj).message
                : 'Haber listesi alınamadı';
        throw new Error(msg);
    }

    const raw = /** @type {{ data?: unknown, metaData?: unknown }} */ (obj)
        .data;
    const list = Array.isArray(raw) ? raw : [];
    const metaData =
        /** @type {{ metaData?: unknown }} */ (obj).metaData ?? null;

    return { list, metaData, obj };
}

/**
 * API bazen `hasNext` / `hasPrevious` alanlarını string döndürüyor; `Boolean("false")` true olur.
 * @param {Record<string, unknown>} obj
 * @param {string} key
 * @returns {boolean | undefined}
 */
function readOptionalBool(obj, key) {
    if (!obj || typeof obj !== 'object' || !(key in obj)) return undefined;
    const v = /** @type {Record<string, unknown>} */ (obj)[key];
    if (v === true || v === 'true' || v === 1 || v === '1') return true;
    if (v === false || v === 'false' || v === 0 || v === '0') return false;
    return undefined;
}

/**
 * @param {unknown} metaData
 * @param {number} itemCountOnPage
 * @param {number} pageNumber
 * @param {number} pageSize
 * @returns {CmsNewsListMeta}
 */
export function parseNewsListMeta(
    metaData,
    itemCountOnPage,
    pageNumber,
    pageSize,
) {
    const m = metaData && typeof metaData === 'object' ? metaData : null;
    if (m) {
        const totalCount = Number(
            /** @type {{ totalCount?: number }} */ (m).totalCount,
        );
        const totalPages = Number(
            /** @type {{ totalPages?: number }} */ (m).totalPages,
        );
        let currentPage = Number(
            /** @type {{ currentPage?: number }} */ (m).currentPage,
        );
        const ps = Number(
            /** @type {{ pageSize?: number }} */ (m).pageSize,
        );
        if (
            Number.isFinite(totalCount) &&
            totalCount >= 0 &&
            Number.isFinite(totalPages) &&
            totalPages >= 1
        ) {
            if (!Number.isFinite(currentPage) || currentPage < 1) {
                currentPage = pageNumber;
            }
            const tp = Math.max(1, totalPages);
            const cp = Math.min(Math.max(1, currentPage), tp);
            const hasNextRaw = readOptionalBool(
                /** @type {Record<string, unknown>} */ (m),
                'hasNext',
            );
            const hasPrevRaw = readOptionalBool(
                /** @type {Record<string, unknown>} */ (m),
                'hasPrevious',
            );
            return {
                totalCount,
                totalPages: tp,
                currentPage: cp,
                pageSize: Number.isFinite(ps) && ps > 0 ? ps : pageSize,
                hasNext:
                    hasNextRaw !== undefined ? hasNextRaw : cp < tp,
                hasPrevious:
                    hasPrevRaw !== undefined ? hasPrevRaw : cp > 1,
            };
        }
    }

    const hasNext = itemCountOnPage === pageSize;
    const hasPrevious = pageNumber > 1;
    const totalPagesGuess =
        itemCountOnPage === 0 && pageNumber === 1
            ? 1
            : hasNext
              ? pageNumber + 1
              : pageNumber;

    return {
        totalCount: (pageNumber - 1) * pageSize + itemCountOnPage,
        totalPages: Math.max(1, totalPagesGuess),
        currentPage: pageNumber,
        pageSize,
        hasNext,
        hasPrevious,
    };
}

/**
 * @param {{ pageNumber?: number, pageSize?: number }} [opts]
 * @returns {Promise<CmsNewsItem[]>}
 */
export async function fetchCmsNewsList(opts = {}) {
    const { list } = await fetchCmsNewsGetAllRaw(opts);
    return processCmsNewsList(list);
}

/**
 * Sayfalı haber listesi (ör. /news).
 * @param {{ pageNumber?: number, pageSize?: number }} [opts]
 * @returns {Promise<{ items: ReturnType<typeof mapCmsNewsToHaberItem>[], meta: CmsNewsListMeta }>}
 */
export async function fetchCmsNewsPaged(opts = {}) {
    const pageNumber = opts.pageNumber ?? 1;
    const pageSize = opts.pageSize ?? 9;
    const { list, metaData } = await fetchCmsNewsGetAllRaw({
        pageNumber,
        pageSize,
    });
    const processed = processCmsNewsList(list);
    const meta = parseNewsListMeta(
        metaData,
        processed.length,
        pageNumber,
        pageSize,
    );
    const items = processed
        .map(mapCmsNewsToHaberItem)
        .filter((x) => x.imageUrl);
    return { items, meta };
}

/**
 * Aktif haberleri sıraya göre döndürür (banner için).
 * @returns {Promise<CmsNewsItem[]>}
 */
export async function fetchCmsNewsForBanner() {
    return fetchCmsNewsList({ pageNumber: 1, pageSize: 10 });
}

/**
 * Ana sayfa banner: `order` alanına göre ilk N haber (kapak görseli olanlar).
 * @param {number} [limit]
 * @returns {Promise<CmsNewsItem[]>}
 */
export async function fetchTopCmsNewsByOrder(limit = 3) {
    const capped = Math.max(1, Math.min(limit, 20));
    const list = await fetchCmsNewsList({ pageNumber: 1, pageSize: 100 });
    return list
        .filter((item) => String(item.coverImageUrl || '').trim())
        .slice(0, capped);
}

/**
 * CMS haber detayı (GetById).
 * @typedef {{
 *   id: string,
 *   title: string,
 *   summary: string,
 *   content: string,
 *   order?: number,
 *   isActive?: boolean,
 *   images?: { id?: string, url?: string, order?: number, createdAtUtc?: string }[],
 *   createdAtUtc?: string,
 *   updatedAtUtc?: string,
 *   coverImageUrl?: string,
 * }} CmsNewsDetail
 */

/**
 * @param {string} id
 * @returns {Promise<CmsNewsDetail>}
 */
export async function fetchCmsNewsById(id) {
    const rawId = String(id || '').trim();
    if (!rawId) {
        throw new Error('Haber kimliği gerekli');
    }
    const params = new URLSearchParams({ id: rawId });
    const url = `${PUBLIC_CMS_API_BASE_URL}/cms/News/GetById?${params}`;
    const res = await fetch(url, {
        method: 'GET',
        headers: {
            Accept: 'application/json',
        },
    });

    const text = await res.text();
    /** @type {unknown} */
    let body;
    try {
        body = text ? JSON.parse(text) : {};
    } catch {
        throw new Error('Haber detayı okunamadı');
    }

    if (!res.ok) {
        const msg =
            body &&
            typeof body === 'object' &&
            'message' in body &&
            typeof /** @type {{ message?: string }} */ (body).message ===
                'string'
                ? /** @type {{ message: string }} */ (body).message
                : null;
        throw new Error(msg || `Haber detayı alınamadı (${res.status})`);
    }

    const obj = body && typeof body === 'object' ? body : {};
    if (/** @type {{ success?: boolean }} */ (obj).success === false) {
        const msg =
            typeof /** @type {{ message?: string | null }} */ (obj).message ===
            'string'
                ? /** @type {{ message: string }} */ (obj).message
                : 'Haber bulunamadı';
        throw new Error(msg);
    }

    const data = /** @type {{ data?: unknown }} */ (obj).data;
    if (!data || typeof data !== 'object') {
        throw new Error('Haber bulunamadı');
    }

    return /** @type {CmsNewsDetail} */ (data);
}

/**
 * @param {CmsNewsDetail} article
 * @returns {string[]}
 */
export function getCmsNewsImageUrlsSorted(article) {
    const list = Array.isArray(article.images) ? article.images : [];
    const withUrl = list
        .filter((x) => x && typeof x === 'object' && x.url)
        .map((x) => ({
            url: resolvePublicCmsMediaUrl(
                /** @type {{ url: string }} */ (x).url,
            ),
            order: Number(/** @type {{ order?: number }} */ (x).order) || 0,
        }));
    withUrl.sort((a, b) => a.order - b.order);
    return [...new Set(withUrl.map((x) => x.url))];
}
